import { threadRenamedEvent } from '#pplx/frontend/apps/ask/events/thread/analytics.js';
import { updateThreadTitleCaches } from '#pplx/frontend/apps/ask/hooks/threads-hooks/threadCacheHelpers.js';
import { setThreadTitleAPI } from '#pplx/frontend/apps/ask/hooks/threads-hooks/useSetThreadTitleAPI.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
export const useSetThreadTitle = ({ title, contextUUID, rwToken, callback, optimisticUpdate, threadRenameAnalytics, executeRename, }) => {
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [titleInternal, setTitleInternal] = useState(title);
    const previousTitleRef = useRef(title);
    const reason = 'thread-item';
    const queryClient = useQueryClient();
    const { session } = useSession();
    const { track } = useInstrumentation();
    const email = session?.user?.email;
    const threadRenameSurface = threadRenameAnalytics?.surface;
    const threadRenameType = threadRenameAnalytics?.threadType;
    useEffect(() => {
        if (title !== previousTitleRef.current && !isEditingTitle) {
            setTitleInternal(title);
            previousTitleRef.current = title;
        }
    }, [isEditingTitle, title]);
    const onClose = useCallback(() => {
        setIsEditingTitle(false);
    }, []);
    const setThreadTitle = useCallback((newTitle, submitCallback) => {
        if (!newTitle) {
            logger.warn('Empty title callback.');
            return;
        }
        if (!contextUUID) {
            logger.warn('No thread UUID found.');
            return;
        }
        const submitOverRest = () => {
            setThreadTitleAPI({
                contextUUID,
                title: newTitle,
                rwToken: rwToken ?? '',
                callback: submitCallback,
                reason,
            });
        };
        if (!executeRename) {
            submitOverRest();
            return;
        }
        executeRename(contextUUID, newTitle)
            .then((handled) => {
            if (handled) {
                submitCallback({});
                return;
            }
            submitOverRest();
        })
            .catch((error) => {
            submitCallback(error instanceof Error ? error : new Error(String(error)));
        });
    }, [contextUUID, executeRename, rwToken, reason]);
    const handleTitleSave = useCallback((newTitle, callbacks) => {
        const nextTitle = newTitle.trim();
        if (nextTitle === titleInternal.trim()) {
            setIsEditingTitle(false);
            return;
        }
        if (!nextTitle) {
            logger.warn('Empty title callback.');
            setIsEditingTitle(false);
            return;
        }
        if (!contextUUID) {
            logger.warn('No thread UUID found.');
            setIsEditingTitle(false);
            return;
        }
        const resolvedContextUUID = contextUUID;
        const update = optimisticUpdate?.(nextTitle);
        const previousTitle = previousTitleRef.current;
        setTitleInternal(nextTitle);
        setIsEditingTitle(false);
        setThreadTitle(nextTitle, (result) => {
            if (result instanceof Error) {
                update?.rollback();
                setTitleInternal(previousTitle);
                callbacks?.onError?.();
                return;
            }
            update?.commit();
            updateThreadTitleCaches(queryClient, resolvedContextUUID, nextTitle, email);
            if (threadRenameSurface && threadRenameType) {
                track(threadRenamedEvent({
                    name: 'thread renamed',
                    data: {
                        contextUUID: resolvedContextUUID,
                        surface: threadRenameSurface,
                        threadType: threadRenameType,
                    },
                }));
            }
            callback?.();
            callbacks?.onSuccess?.();
        });
    }, [
        setThreadTitle,
        titleInternal,
        contextUUID,
        optimisticUpdate,
        email,
        queryClient,
        threadRenameSurface,
        threadRenameType,
        track,
        callback,
    ]);
    const onRenameClick = useCallback(() => {
        setIsEditingTitle(true);
    }, [setIsEditingTitle]);
    return {
        handleTitleSave,
        onRenameClick,
        onClose,
        isEditingTitle,
        title: titleInternal,
    };
};
//# sourceMappingURL=useSetThreadTitle.js.map